import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LogoMark } from "./Logo";
import { useContent } from "../lib/ContentContext";

const NODES = [
  { key: "indril", to: "/indril", x: 50, y: 10, color: "#00f0ff" },
  { key: "usb", to: "/urgent-sell-buy", x: 14, y: 76, color: "#d4af37" },
  { key: "aios", to: "/boss-ai-os", x: 86, y: 76, color: "#3b82f6" },
];

export const Ecosystem = () => {
  const { content } = useContent();
  const [active, setActive] = useState("indril");
  const p = content.platforms[active];
  return (
    <div className="grid lg:grid-cols-12 gap-10 items-center" data-testid="ecosystem-visual">
      <div className="lg:col-span-7 relative aspect-square max-w-[620px] w-full mx-auto">
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden="true">
          <circle cx="50" cy="54" r="40" fill="none" stroke="rgba(212,175,55,0.18)" strokeWidth="0.2" />
          <circle cx="50" cy="54" r="28" fill="none" stroke="rgba(0,102,255,0.25)" strokeWidth="0.2" strokeDasharray="1 1.5" />
          {NODES.map((n) => (
            <g key={n.key}>
              <line x1="50" y1="54" x2={n.x} y2={n.y} stroke={n.color} strokeOpacity={active === n.key ? 0.9 : 0.25} strokeWidth="0.3" className="dash-flow" />
              <circle r="0.9" fill={n.color}>
                <animateMotion dur={`${3 + NODES.indexOf(n)}s`} repeatCount="indefinite" path={`M50 54 L${n.x} ${n.y}`} />
              </circle>
            </g>
          ))}
          <line x1="50" y1="10" x2="14" y2="76" stroke="rgba(255,255,255,0.08)" strokeWidth="0.2" />
          <line x1="14" y1="76" x2="86" y2="76" stroke="rgba(255,255,255,0.08)" strokeWidth="0.2" />
          <line x1="86" y1="76" x2="50" y2="10" stroke="rgba(255,255,255,0.08)" strokeWidth="0.2" />
        </svg>
        <div className="absolute left-1/2 top-[54%] -translate-x-1/2 -translate-y-1/2 w-[26%] aspect-square rounded-full glass flex flex-col items-center justify-center shadow-[0_0_60px_rgba(212,175,55,0.25)]">
          <LogoMark className="w-1/2 h-1/2" />
          <span className="font-mono text-[9px] sm:text-[10px] tracking-[0.2em] text-[#e7d48f] mt-1">BOSS EMPIRE</span>
        </div>
        {NODES.map((n) => (
          <button key={n.key} onMouseEnter={() => setActive(n.key)} onFocus={() => setActive(n.key)} onClick={() => setActive(n.key)}
            data-testid={`ecosystem-node-${n.key}`} aria-pressed={active === n.key}
            style={{ left: `${n.x}%`, top: `${n.y}%`, borderColor: active === n.key ? n.color : "rgba(255,255,255,0.12)" }}
            className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full border bg-[#07080d]/90 backdrop-blur px-4 sm:px-5 py-2.5 min-h-[44px] font-display text-xs sm:text-sm whitespace-nowrap transition-[transform,border-color,color] duration-300 ${active === n.key ? "scale-110 text-white" : "text-slate-300 hover:text-white"}`}>
            {content.platforms[n.key].name}
          </button>
        ))}
      </div>
      <div className="lg:col-span-5 min-h-[260px] relative">
        <AnimatePresence initial={false}>
          <motion.div key={active} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, position: "absolute", top: 0 }} transition={{ duration: 0.45 }} data-testid="ecosystem-detail">
            <p className="eyebrow mb-4">{p.category}</p>
            <h3 className="font-display text-4xl sm:text-5xl font-bold">{p.name}</h3>
            <p className="mt-6 text-slate-300 leading-relaxed">{p.description}</p>
            <Link to={NODES.find((n) => n.key === active).to} data-testid="ecosystem-detail-link"
              className="mt-8 inline-flex items-center gap-2 text-[#f3d77a] border-b border-[#d4af37]/40 pb-1 hover:text-white hover:border-white transition-colors">
              Explore {p.name} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
